import React, { useState } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { apiPost } from "../lib/api";
import MotionButton from "./motion/MotionButton";
import AnswerOptions from "./AnswerOptions";
import QuestionFeedback from "./QuestionFeedback";

const difficultyClass = (difficulty) => {
  if (difficulty === "easy") return "bg-brand-green/10 text-brand-green";
  if (difficulty === "hard") return "bg-brand-red/10 text-brand-red";
  return "bg-brand-gold/15 text-brand-gold";
};

const submitLabel = ({ submitting, isExam }) => {
  if (submitting) return "Invio...";
  if (isExam) return "Conferma";
  return "Verifica risposta";
};

function QuestionHeader({ question, isFavorite, onToggleFavorite }) {
  return (
    <div className="px-6 py-4 border-b border-brand-borderSubtle flex items-center justify-between flex-wrap gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span data-testid="question-specialty" className="px-2.5 py-1 rounded-md bg-brand-green/10 text-brand-green eyebrow text-[10px]">
          {question.specialty}
        </span>
        <span className="px-2.5 py-1 rounded-md bg-brand-surfaceElevated text-brand-secondary eyebrow text-[10px]">
          {question.topic}
        </span>
        <span className={`px-2.5 py-1 rounded-md eyebrow text-[10px] ${difficultyClass(question.difficulty)}`}>
          {question.difficulty}
        </span>
      </div>
      {onToggleFavorite && (
        <motion.button
          data-testid="favorite-toggle-btn"
          whileTap={{ scale: 0.9 }}
          onClick={() => onToggleFavorite(question.question_id, !isFavorite)}
          className={`text-sm flex items-center gap-1.5 transition-colors ${
            isFavorite ? "text-brand-gold" : "text-brand-secondary hover:text-brand-gold"
          }`}
        >
          <Star className={`h-4 w-4 ${isFavorite ? "fill-brand-gold" : ""}`} />
          {isFavorite ? "Salvato" : "Salva"}
        </motion.button>
      )}
    </div>
  );
}

function QuestionActions({ result, showNext, isExam, submitting, selected, onSubmit, onNext }) {
  return (
    <div className="mt-7 flex flex-col sm:flex-row gap-3">
      {!result && (
        <MotionButton
          data-testid="submit-answer-btn"
          variant="primary"
          size="md"
          disabled={selected === null || submitting}
          onClick={onSubmit}
        >
          {submitLabel({ submitting, isExam })}
        </MotionButton>
      )}
      {result && showNext && (
        <MotionButton data-testid="next-question-btn" variant="secondary" onClick={onNext}>
          Prossima domanda
        </MotionButton>
      )}
    </div>
  );
}

export default function QuestionView({
  question, mode = "training", examId,
  onAnswered, onNext, showNext = true,
  isFavorite = false, onToggleFavorite, forceResult,
}) {
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(forceResult || null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (selected === null || result) return;
    setSubmitting(true);
    try {
      const r = await apiPost("/answers/submit", {
        question_id: question.question_id,
        selected_index: selected,
        mode,
        exam_id: examId,
      });
      setResult(r);
      onAnswered?.(r);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    setSelected(null);
    setResult(null);
    onNext?.();
  };

  const isExam = mode === "exam";
  const showFeedback = result && !isExam;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="premium-card"
    >
      <QuestionHeader question={question} isFavorite={isFavorite} onToggleFavorite={onToggleFavorite} />
      <div className="p-6 sm:p-8">
        <p data-testid="question-stem" className="font-serif text-xl sm:text-2xl text-brand-primary leading-relaxed">
          {question.stem}
        </p>
        <AnswerOptions
          question={question}
          selected={selected}
          setSelected={setSelected}
          result={result}
          showFeedback={showFeedback}
          submitting={submitting}
        />
        <QuestionActions
          result={result}
          showNext={showNext}
          isExam={isExam}
          submitting={submitting}
          selected={selected}
          onSubmit={submit}
          onNext={handleNext}
        />
        {showFeedback && <QuestionFeedback question={question} result={result} />}
      </div>
    </motion.div>
  );
}
